import { type RemoteIntroPromoCohort } from '@heidi/growth/contracts';
import { type MessageDescriptor, defineMessages } from 'react-intl';

/**
 * Copy registry for the Heidi Remote intro promo, keyed by cohort.
 *
 * Each cohort gets its own modal heading / description / feature bullets /
 * CTA label / footer caption, plus banner + profile-menu-card variants.
 * Bullets that need price placeholders (e.g. `Order for <strike>{rrp}</strike>
 * {discounted}* inc. shipping`) use ICU rich-text — the consuming component
 * is responsible for the strike-through render fn and pricing-lookup values.
 *
 * Wording is lifted from Figma node 11164-15005 (Monthly / Yearly / Free user
 * offer modals at 11164:13106 / 11164:13163 / 11164:13220).
 */

// ── Shared strings ────────────────────────────────────────────────────────────

export const remoteIntroPromoSharedMessages = defineMessages({
  /** Green pill rendered at the top of every modal. */
  offerEndsBadge: {
    defaultMessage: 'Offer ends 31 May',
    id: 'LcN7rYmtJS',
    description: 'Pill shown above the headline on every Remote intro promo modal',
  },
  /** Accessible close-button label. */
  dismissAriaLabel: {
    defaultMessage: 'Dismiss promo',
    id: 'CbTQNE5ppR',
    description: 'aria-label for the banner / modal close affordance',
  },
});

// ── Monthly cohort ────────────────────────────────────────────────────────────

const monthly = defineMessages({
  modalHeading: {
    defaultMessage: '50% off Heidi Remote when you switch to yearly',
    id: 'iGxb5KIpsX',
    description: 'Modal headline shown to monthly Scribe-Plus / Clinician subscribers',
  },
  modalDescription: {
    defaultMessage:
      'Heidi Remote is the first mic built exclusively for scribing. Works on Heidi mobile and desktop apps.',
    id: 'Zga7f3AEL0',
    description: 'Modal lead-in copy for monthly cohort',
  },
  /** Only rendered for Clinician-tier users (Scribe-Plus users skip this bullet). */
  bulletYearlySavings: {
    defaultMessage: 'Switch to Clinician yearly plan and save {yearlySavings}',
    id: 'NOYx7GFQgE',
    description: 'Yearly-plan savings bullet shown only for Clinician-tier monthly users',
  },
  bulletPrice: {
    defaultMessage: 'Order for <strike>{rrp}</strike> {discounted}* inc. shipping',
    id: 'Wg3yeKzkoO',
    description: 'Crossed-out RRP + discounted price bullet for monthly cohort',
  },
  bulletBattery: {
    defaultMessage: '14-hour battery life. Works offline.',
    id: 'ZH3NM29MMK',
    description: 'Generic feature bullet shared across cohorts',
  },
  cta: {
    defaultMessage: 'Switch to yearly',
    id: 'DCPWzCDO9D',
    description: 'Primary CTA — opens in-app annual-upgrade flow (not the storefront)',
  },
  footerCaption: {
    defaultMessage:
      '*50% off Heidi Remote available when switching from monthly to yearly billing, for users who have not previously purchased a Heidi Remote. Promo code will be emailed once yearly billing is active. Excludes applicable sales taxes. Promotion can only be applied once and is non-transferable. Promotion valid until 31 May 2026. Expected delivery timeframe is up to 8–10 weeks for all purchases made from 13 April 2026.',
    id: '+vVv+OGoCm',
    description: 'Fine-print disclaimer below the monthly cohort modal CTA',
  },
  bannerTitle: {
    defaultMessage: 'Switch to yearly, get 50% off Remote',
    id: 'XG2xPgY9wN',
    description: 'Banner title on Billing page for monthly cohort (Figma 11164-13875)',
  },
  bannerDescription: {
    defaultMessage: 'Capture every visit, wherever you are. Offer ends 31 May 2026.',
    id: 'zvd504Gguv',
    description: 'Banner subtitle on Billing page — shared across cohorts',
  },
  menuCardTitle: {
    defaultMessage: 'Get 50% off Remote',
    id: 'YYQ8Bx2kOU',
    description: 'Profile-menu card title for monthly cohort (Figma 11164-14730)',
  },
  menuCardDescription: {
    defaultMessage: 'Switch to a yearly plan and save on the new Heidi Remote.',
    id: '0Kv9lkUxCl',
    description: 'Profile-menu card description for monthly cohort',
  },
});

// ── Yearly cohort ─────────────────────────────────────────────────────────────

const yearly = defineMessages({
  modalHeading: {
    defaultMessage: '50% off Heidi Remote, to say thank you',
    id: 'OoqENGxCrP',
    description: 'Modal headline shown to yearly Scribe-Plus / Clinician subscribers',
  },
  modalDescription: {
    defaultMessage:
      'As a yearly Heidi subscriber, we’re offering a discount on <link>Heidi Remote</link>, the first mic built exclusively for scribing.',
    id: 'BynkWFsS6x',
    description:
      'Modal lead-in copy for yearly cohort — `<link>` underlines the product name inline.',
  },
  bulletBattery: {
    defaultMessage: '14-hour battery life. Works offline.',
    id: 'uL+g/Gn7Oy',
    description: 'Generic feature bullet (yearly cohort)',
  },
  bulletCompatibility: {
    defaultMessage: 'Compatible with Heidi mobile and desktop apps',
    id: 'Q2ZS+ih4hF',
    description: 'Platform-compatibility bullet (yearly cohort)',
  },
  bulletPrice: {
    defaultMessage: 'Order for <strike>{rrp}</strike> {discounted}* inc. shipping',
    id: 'RqiDxSLs/O',
    description: 'Crossed-out RRP + discounted price bullet for yearly cohort',
  },
  cta: {
    defaultMessage: 'Order today',
    id: 'kyVMw20naP',
    description: 'Primary CTA — opens Heidi Remote storefront in a new tab',
  },
  footerCaption: {
    defaultMessage:
      '*Loyalty pricing available to current annual subscribers who have not previously purchased a Heidi Remote. 50% discount for annual subscribers. Excludes applicable sales taxes. Promotion can only be applied once and is non-transferable. Promotion valid until 31 May 2026. Expected delivery timeframe is up to 8–10 weeks for all purchases made from 13 April 2026.',
    id: 'f5uUNnpvYG',
    description: 'Fine-print disclaimer below the yearly cohort modal CTA',
  },
  bannerTitle: {
    defaultMessage: 'Save 50% off the new Heidi Remote',
    id: 'BLeY3jg1ko',
    description: 'Banner title on Billing page for yearly cohort (Figma 11164-13840)',
  },
  bannerDescription: {
    defaultMessage: 'Capture every visit, wherever you are. Offer ends 31 May 2026.',
    id: 'zvd504Gguv',
    description: 'Banner subtitle on Billing page — shared across cohorts',
  },
  menuCardTitle: {
    defaultMessage: 'Get 50% off Remote',
    id: 'aKTCHdzM92',
    description: 'Profile-menu card title for yearly cohort (Figma 11164-14759)',
  },
  menuCardDescription: {
    defaultMessage: 'Capture every visit, wherever you are. Offer ends 31 May 2026.',
    id: 'Saf4chlCwQ',
    description: 'Profile-menu card description for yearly cohort',
  },
});

// ── Free / trialling cohort ───────────────────────────────────────────────────

const free = defineMessages({
  modalHeading: {
    defaultMessage: 'Buy Heidi Remote, get one month free',
    id: 'CEFhXIuOhc',
    description: 'Modal headline shown to free / trialling users',
  },
  modalDescription: {
    defaultMessage:
      'Heidi Remote is the first mic built exclusively for scribing. Works on Heidi mobile and desktop apps.',
    id: 'WYp77oQSM4',
    description: 'Modal lead-in copy for free cohort',
  },
  bulletBattery: {
    defaultMessage: '14-hour battery life. Works offline.',
    id: '9YNdHLHTWS',
    description: 'Generic feature bullet (free cohort)',
  },
  bulletPrice: {
    defaultMessage: 'Order for <strike>{rrp}</strike> {introPrice}* inc. shipping',
    id: 'bm0HhQzxqR',
    description: 'Crossed-out RRP + intro price bullet for free cohort',
  },
  bulletClinicianFree: {
    defaultMessage: 'Get one month of the Clinician Plan free',
    id: 'x20eXQ6Twr',
    description: 'Bundle bullet — Clinician month included with Remote purchase',
  },
  cta: {
    defaultMessage: 'Order today',
    id: 'kyVMw20naP',
    description: 'Primary CTA — opens Heidi Remote storefront in a new tab',
  },
  footerCaption: {
    defaultMessage:
      '*Promo code to redeem will be sent upon device arrival. Introductory promo pricing valid until 31 May 2026. Excludes applicable sales taxes. Promotion can only be applied once and is non-transferable. Expected delivery timeframe is up to 8–10 weeks for all purchases made from 13 April 2026.',
    id: '3zBPrWdf6X',
    description: 'Fine-print disclaimer below the free cohort modal CTA',
  },
  bannerTitle: {
    defaultMessage: 'Buy Heidi Remote, get 1 month free on the Clinician plan',
    id: '6wnSgMNrrK',
    description: 'Banner title on Billing page for free cohort (Figma 11164-14817)',
  },
  bannerDescription: {
    defaultMessage: 'Capture every visit, wherever you are. Offer ends 31 May 2026.',
    id: 'zvd504Gguv',
    description: 'Banner subtitle on Billing page — shared across cohorts',
  },
  menuCardTitle: {
    defaultMessage: 'Buy Remote and save',
    id: 'JEAbVZ7b4y',
    description: 'Profile-menu card title for free cohort (Figma 11164-14788)',
  },
  menuCardDescription: {
    defaultMessage:
      'Order the new Heidi Remote and get a month free on the Clinician Plan.',
    id: 'NGy6vUa6xo',
    description: 'Profile-menu card description for free cohort',
  },
});

// ── Registry ──────────────────────────────────────────────────────────────────

export type RemoteIntroPromoCohortMessages = {
  modalHeading: MessageDescriptor;
  modalDescription: MessageDescriptor;
  bulletPrice: MessageDescriptor;
  cta: MessageDescriptor;
  footerCaption: MessageDescriptor;
  bannerTitle: MessageDescriptor;
  bannerDescription: MessageDescriptor;
  menuCardTitle: MessageDescriptor;
  menuCardDescription: MessageDescriptor;
};

export const remoteIntroPromoMessages: Record<
  RemoteIntroPromoCohort,
  typeof monthly | typeof yearly | typeof free
> = {
  monthly_subscriber: monthly,
  yearly_subscriber: yearly,
  free_user: free,
};

export {
  free as remoteIntroPromoFreeMessages,
  monthly as remoteIntroPromoMonthlyMessages,
  yearly as remoteIntroPromoYearlyMessages,
};
